import { IAggregatedColumn, getBindingForRow, isFilterableColumn } from "../../utilities/AggregatedColumnHelper";
import { isNumericEqBinding } from "../Body/aggregatedFilterCaml";
import { AGGREGATE_SOURCE_LIST_ID } from "../Body/helpers/aggregateRowFields";
import type { IAggregatedColumnFilter } from "../Body/useAggregatedDocuments";
import type { FilterItem, FilterSection } from "../../src/taskpane/redux/models/reduxModels";

/**
 * Pure section-building logic for CMAggregatedFilterPanel — kept free of the data
 * hook's auth-bound import chain so it stays unit-testable (type-only imports are
 * erased at compile time).
 */

export const MAX_SUGGESTED_VALUES = 50;
// Sanity valve for the union of row-harvested + server-discovered + applied values.
// Selected values are always retained; only unselected suggestions are trimmed.
export const MAX_SECTION_ITEMS = 200;

// Dedupe/selection identity: case- and whitespace-insensitive on the WIRE value.
// CAML text Eq is case-insensitive server-side, so values differing only by case are
// one filter value; without this, two libraries storing "Open"/"OPEN" render
// near-duplicate checkboxes with only the exact-case one checked.
const normalizeValueKey = (value: string): string => value?.trim().toLowerCase() ?? "";

export const valueLabel = (rawValue: any): string[] => {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
        return [];
    }
    if (Array.isArray(rawValue)) {
        return rawValue
            .map(entry => entry?.Label ?? entry?.lookupValue ?? entry?.title ?? entry?.value ?? entry)
            .filter(label => typeof label === "string" && label.length > 0);
    }
    if (typeof rawValue === "object") {
        const label = rawValue?.Label ?? rawValue?.lookupValue ?? rawValue?.title ?? rawValue?.value;
        return typeof label === "string" && label.length > 0 ? [label] : [];
    }
    return [String(rawValue)];
};

/**
 * Trims an item union to MAX_SECTION_ITEMS and orders it for display.
 *
 * Selected values are never trimmed — a checked box vanishing because the section grew
 * past the valve would leave a filter the user cannot see or clear.
 */
const trimAndSortItems = (
    list: { title: string; value: string }[],
    activeKeys: Set<string>
): FilterItem[] => {
    let trimmed = list;
    if (trimmed.length > MAX_SECTION_ITEMS) {
        const selected = trimmed.filter(entry => activeKeys.has(normalizeValueKey(entry.value)));
        const unselected = trimmed
            .filter(entry => !activeKeys.has(normalizeValueKey(entry.value)))
            .slice(0, Math.max(0, MAX_SECTION_ITEMS - selected.length));
        trimmed = [...selected, ...unselected];
    }

    return trimmed
        .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }))
        .map(entry => ({
            title: entry.title,
            value: entry.value,
            selected: activeKeys.has(normalizeValueKey(entry.value)),
        }));
};

/**
 * Folds server-discovered values into a section, on top of whatever the loaded rows
 * already contributed.
 *
 * Existing items win on collision so a row-harvested title (which may carry the display
 * form of a numeric or lookup value) is not overwritten by the wire form the filter
 * endpoint returns. Selection is preserved from the current items rather than recomputed:
 * this runs between rebuilds, and the applied filters are not an input here.
 */
export const mergeDiscoveredValues = (section: FilterSection, discovered: string[]): FilterSection => {
    const activeKeys = new Set(
        (section?.items ?? []).filter(item => item.selected).map(item => normalizeValueKey(item.value ?? item.title))
    );

    const entries = new Map<string, { title: string; value: string }>();
    for (const item of section?.items ?? []) {
        const value = item.value ?? item.title;
        const key = normalizeValueKey(value);
        if (key && !entries.has(key)) {
            entries.set(key, { title: item.title, value });
        }
    }
    for (const value of discovered ?? []) {
        const key = normalizeValueKey(value);
        if (key && !entries.has(key)) {
            entries.set(key, { title: value, value });
        }
    }

    return { ...section, items: trimAndSortItems(Array.from(entries.values()), activeKeys) };
};

/**
 * Builds one FilterSection per filterable merged column, plus one section per
 * ORPHANED applied filter (its column left `mergedColumns` — e.g. a schema change or
 * a library set change): without that section the filter would keep emptying the grid
 * with no way to uncheck it from the panel.
 *
 * Item titles are the union of values harvested from the loaded rows (capped),
 * values already present in the section (keeps server-search discoveries alive across
 * rebuilds) and the applied filter values (a checked value must stay visible even
 * when its rows scrolled out of the loaded window). For numeric bindings the wire
 * value comes from the invariant dotted raw twin (`row["<name>."]`) — display strings
 * are locale-formatted ("1.234" in nl/de) and misparse; the display string stays the
 * item TITLE. Selection state derives from the APPLIED filters — the panel applies
 * immediately, so there is no draft to reconcile.
 */
export const buildAggregatedFilterSections = (
    mergedColumns: IAggregatedColumn[],
    rows: any[],
    filters: IAggregatedColumnFilter[],
    previousSections: FilterSection[]
): FilterSection[] => {
    const sections = (mergedColumns ?? [])
        .filter(isFilterableColumn)
        .map(column => {
            const previous = previousSections?.find(section => section.filterField.RealFieldName === column.key);
            const active = filters?.find(filter => filter.columnKey === column.key);
            const activeKeys = new Set((active?.values ?? []).map(normalizeValueKey));

            // key (normalized wire value) → {title, value}; first-seen casing/format wins.
            const entries = new Map<string, { title: string; value: string }>();
            const addEntry = (title: string, value: string): void => {
                const key = normalizeValueKey(value);
                if (!key) return;
                if (!entries.has(key)) {
                    entries.set(key, { title, value });
                }
            };

            for (const row of rows ?? []) {
                const binding = getBindingForRow(column, row?.[AGGREGATE_SOURCE_LIST_ID]);
                if (!binding) continue;
                const displayValue = row[binding.internalName];
                const rawTwin = row[`${binding.internalName}.`];
                if (isNumericEqBinding(binding) && rawTwin !== null && rawTwin !== undefined && rawTwin !== "") {
                    const title = valueLabel(displayValue)[0] ?? String(rawTwin);
                    addEntry(title, String(rawTwin));
                } else {
                    for (const label of valueLabel(displayValue)) {
                        addEntry(label, label);
                        if (entries.size >= MAX_SUGGESTED_VALUES) break;
                    }
                }
                if (entries.size >= MAX_SUGGESTED_VALUES) break;
            }
            previous?.items?.forEach(item => addEntry(item.title, item.value ?? item.title));
            (active?.values ?? []).forEach(value => addEntry(value, value));

            const items: FilterItem[] = trimAndSortItems(Array.from(entries.values()), activeKeys);

            // The section component reads DisplayName/RealFieldName/Type/FieldType.
            // Filterable columns are never DateTime, so the date-slider path is inert.
            const firstBinding = Object.values(column.bindings ?? {})[0];
            return {
                items,
                filterField: {
                    DisplayName: column.displayName,
                    RealFieldName: column.key,
                    Type: firstBinding?.type,
                    FieldType: firstBinding?.type,
                },
                showMoreExpanded: previous?.showMoreExpanded ?? false,
                hasSlider: false,
                showSlider: false,
            };
        });

    // Orphaned applied filters (column no longer merged/filterable) stay visible and
    // uncheckable; their values render selected so the user can clear them.
    const sectionKeys = new Set(sections.map(section => section.filterField.RealFieldName));
    for (const filter of filters ?? []) {
        if (sectionKeys.has(filter.columnKey) || (filter.values?.length ?? 0) === 0) {
            continue;
        }
        const previous = previousSections?.find(section => section.filterField.RealFieldName === filter.columnKey);
        sections.push({
            items: filter.values.map(value => ({ title: value, value, selected: true })),
            filterField: {
                DisplayName: filter.displayName,
                RealFieldName: filter.columnKey,
                Type: "Text",
                FieldType: "Text",
            },
            showMoreExpanded: previous?.showMoreExpanded ?? false,
            hasSlider: false,
            showSlider: false,
        });
    }

    return sections;
};
